/**
 * @fileoverview useLayoutHotkeys Hook
 * 
 * Custom hook that manages all keyboard shortcuts for the SessionViewer layout.
 * Handles split view adjustments, fullscreen toggle, and panel controls.
 * 
 * @module hooks/useLayoutHotkeys
 */

import { useHotkeys } from 'react-hotkeys-hook';
import CONSTANTS from '../config/constants';

/**
 * useLayoutHotkeys Hook
 * 
 * Registers all keyboard shortcuts for layout management including:
 * - Details panel split control (Ctrl+1-9, Ctrl+0)
 * - Video panel split control (Alt+1-9)
 * - Combined split control (Ctrl+Alt+1-9, Ctrl+Alt+0)
 * - Fullscreen toggle (Alt+F)
 * - Details panel toggle (Ctrl+D)
 * - Annotations modal toggle (Alt+A)
 * 
 * @param {Object} params - Hook parameters
 * @param {Function} params.setVerticalSplit - Function to set vertical split percentage
 * @param {Object} params.LayoutStore - Layout store instance
 * @param {Object} params.PlayerStore - Player store instance
 * @param {boolean} params.isCollapsed - Whether details panel is collapsed
 * @param {Object} params.annotationsModal - Annotations modal controller
 */
export function useLayoutHotkeys({
  setVerticalSplit,
  LayoutStore,
  PlayerStore,
  isCollapsed,
  annotationsModal,
}) {
  // ============================================================================
  // DETAILS PANEL CONTROL (Ctrl+1-9, Ctrl+0)
  // ============================================================================
  
  // Ctrl+1: Set details panel to 10%
  useHotkeys(
    'ctrl+1',
    () => {
      setVerticalSplit(10);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );

  // Ctrl+2: Set details panel to 20%
  useHotkeys(
    'ctrl+2',
    () => {
      setVerticalSplit(20);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );

  // Ctrl+3: Set details panel to 30%
  useHotkeys(
    'ctrl+3',
    () => {
      setVerticalSplit(30);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );

  // Ctrl+4: Set details panel to 40%
  useHotkeys(
    'ctrl+4',
    () => {
      setVerticalSplit(40);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );

  // Ctrl+5: Set details panel to 50%
  useHotkeys(
    'ctrl+5',
    () => {
      setVerticalSplit(50);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );

  // Ctrl+6: Set details panel to 60%
  useHotkeys(
    'ctrl+6',
    () => {
      setVerticalSplit(60);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );

  // Ctrl+7: Set details panel to 70%
  useHotkeys(
    'ctrl+7',
    () => {
      setVerticalSplit(70);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );

  // Ctrl+8: Set details panel to 80%
  useHotkeys(
    'ctrl+8',
    () => {
      setVerticalSplit(80);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );

  // Ctrl+9: Set details panel to 90%
  useHotkeys(
    'ctrl+9',
    () => {
      setVerticalSplit(90);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );

  // Ctrl+0: Collapse details panel
  useHotkeys('ctrl+0', () => LayoutStore.detailsSplitView.collapse());

  // ============================================================================
  // VIDEO PANEL CONTROL (Alt+1-9)
  // ============================================================================

  // Alt+1: Set video panel to 10%
  useHotkeys(
    'alt+1',
    () => {
      PlayerStore.setVideoPanelSplitPercentage(10);
    },
    { enableOnContentEditable: true }
  );

  // Alt+2: Set video panel to 20%
  useHotkeys(
    'alt+2',
    () => {
      PlayerStore.setVideoPanelSplitPercentage(20);
    },
    { enableOnContentEditable: true }
  );

  // Alt+3: Set video panel to 30%
  useHotkeys(
    'alt+3',
    () => {
      PlayerStore.setVideoPanelSplitPercentage(30);
    },
    { enableOnContentEditable: true }
  );

  // Alt+4: Set video panel to 40%
  useHotkeys(
    'alt+4',
    () => {
      PlayerStore.setVideoPanelSplitPercentage(40);
    },
    { enableOnContentEditable: true }
  );

  // Alt+5: Set video panel to 50%
  useHotkeys(
    'alt+5',
    () => {
      PlayerStore.setVideoPanelSplitPercentage(50);
    },
    { enableOnContentEditable: true }
  );

  // Alt+6: Set video panel to 60%
  useHotkeys(
    'alt+6',
    () => {
      PlayerStore.setVideoPanelSplitPercentage(60);
    },
    { enableOnContentEditable: true }
  );

  // Alt+7: Set video panel to 70%
  useHotkeys(
    'alt+7',
    () => {
      PlayerStore.setVideoPanelSplitPercentage(70);
    },
    { enableOnContentEditable: true }
  );

  // Alt+8: Set video panel to 80%
  useHotkeys(
    'alt+8',
    () => {
      PlayerStore.setVideoPanelSplitPercentage(80);
    },
    { enableOnContentEditable: true }
  );

  // Alt+9: Set video panel to 90%
  useHotkeys(
    'alt+9',
    () => {
      PlayerStore.setVideoPanelSplitPercentage(90);
    },
    { enableOnContentEditable: true }
  );

  // ============================================================================
  // COMBINED CONTROL (Ctrl+Alt+1-9, Ctrl+Alt+0)
  // ============================================================================

  // Ctrl+Alt+1: Set both panels to 10%
  useHotkeys(
    'ctrl+alt+1',
    () => {
      setVerticalSplit(10);
      PlayerStore.setVideoPanelSplitPercentage(10);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );

  // Ctrl+Alt+2: Set both panels to 20%
  useHotkeys(
    'ctrl+alt+2',
    () => {
      setVerticalSplit(20);
      PlayerStore.setVideoPanelSplitPercentage(20);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );

  // Ctrl+Alt+3: Set both panels to 30%
  useHotkeys(
    'ctrl+alt+3',
    () => {
      setVerticalSplit(30);
      PlayerStore.setVideoPanelSplitPercentage(30);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );

  // Ctrl+Alt+4: Set both panels to 40%
  useHotkeys(
    'ctrl+alt+4',
    () => {
      setVerticalSplit(40);
      PlayerStore.setVideoPanelSplitPercentage(40);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );

  // Ctrl+Alt+5: Set both panels to 50%
  useHotkeys(
    'ctrl+alt+5',
    () => {
      setVerticalSplit(50);
      PlayerStore.setVideoPanelSplitPercentage(50);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );

  // Ctrl+Alt+6: Set both panels to 60%
  useHotkeys(
    'ctrl+alt+6',
    () => {
      setVerticalSplit(60);
      PlayerStore.setVideoPanelSplitPercentage(60);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );

  // Ctrl+Alt+7: Set both panels to 70%
  useHotkeys(
    'ctrl+alt+7',
    () => {
      setVerticalSplit(70);
      PlayerStore.setVideoPanelSplitPercentage(70);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );

  // Ctrl+Alt+8: Set both panels to 80%
  useHotkeys(
    'ctrl+alt+8',
    () => {
      setVerticalSplit(80);
      PlayerStore.setVideoPanelSplitPercentage(80);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );

  // Ctrl+Alt+9: Set both panels to 90%
  useHotkeys(
    'ctrl+alt+9',
    () => {
      setVerticalSplit(90);
      PlayerStore.setVideoPanelSplitPercentage(90);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );

  // Ctrl+Alt+0: Collapse details, set video to 20%
  useHotkeys(
    'ctrl+alt+0',
    () => {
      setVerticalSplit(100);
      PlayerStore.setVideoPanelSplitPercentage(20);
      LayoutStore.detailsSplitView.collapse();
    },
    { enableOnContentEditable: true }
  );

  // ============================================================================
  // OTHER CONTROLS
  // ============================================================================

  // Alt+F: Toggle fullscreen
  useHotkeys(
    'alt+f',
    () => {
      if (LayoutStore.isFullScreen) {
        LayoutStore.exitFullScreen();
      } else {
        LayoutStore.enterFullScreen();
      }
    },
    { enableOnContentEditable: true }
  );

  // Ctrl+D: Toggle details panel
  useHotkeys(
    CONSTANTS.KEYBOARD_SHORTCUTS.VIEW.OPTIONS.TOGGLE_DETAILS.KEY,
    () => {
      if (isCollapsed) {
        LayoutStore.detailsSplitView.expand();
      } else {
        LayoutStore.detailsSplitView.collapse();
      }
    },
    { enableOnContentEditable: true }
  );

  // Alt+A: Toggle annotations modal
  useHotkeys(
    CONSTANTS.KEYBOARD_SHORTCUTS.ANNOTATIONS.OPTIONS.FLOATING_MODAL.KEY,
    () => {
      annotationsModal.toggleModal();
    },
    { enableOnContentEditable: true }
  );
}
